/* Browser push helpers for the PLUSMAKER web app.
   Permission is requested ONLY from a clear user action (the Enable button).
   No implementation details (VAPID / service worker) are exposed in copy. */

import api from "@/lib/api";

export function pushSupport() {
  if (typeof window === "undefined") return "unsupported";
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    return "unsupported";
  }
  if (window.Notification.permission === "denied") return "denied";
  return "supported";
}

export async function getPushState() {
  const support = pushSupport();
  if (support !== "supported") return { support, subscribed: false };
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    return { support, subscribed: !!sub };
  } catch {
    return { support, subscribed: false };
  }
}

function b64ToUint8(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

function bufToB64(buf) {
  return window
    .btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export async function enablePush() {
  const support = pushSupport();
  if (support === "unsupported") return { ok: false, reason: "unsupported" };
  if (support === "denied") return { ok: false, reason: "denied" };
  try {
    const permission = await window.Notification.requestPermission();
    if (permission !== "granted") {
      return { ok: false, reason: permission === "denied" ? "denied" : "dismissed" };
    }
    const { data } = await api.get("/web-push/config");
    if (!data.enabled || !data.public_key) return { ok: false, reason: "unavailable" };
    const reg = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64ToUint8(data.public_key),
    });
    await api.post("/web-push/subscribe", {
      endpoint: sub.endpoint,
      p256dh: bufToB64(sub.getKey("p256dh")),
      auth: bufToB64(sub.getKey("auth")),
      user_agent: navigator.userAgent,
    });
    return { ok: true };
  } catch {
    return { ok: false, reason: "failed" };
  }
}

export async function syncPushAccount() {
  // Account-switch safety: rebind an EXISTING browser subscription to the currently
  // authenticated account. Never prompts for permission and never creates a new
  // subscription — if none exists we do nothing. The backend authenticates the caller
  // and only rebinds when the presented subscription keys match the stored ones
  // (device-possession proof), so it never steals another device's subscription.
  try {
    if (pushSupport() !== "supported") return;
    const reg = await navigator.serviceWorker.getRegistration("/");
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (!sub) return;
    await api.post("/web-push/subscribe", {
      endpoint: sub.endpoint,
      p256dh: bufToB64(sub.getKey("p256dh")),
      auth: bufToB64(sub.getKey("auth")),
      user_agent: navigator.userAgent,
    });
  } catch { /* best-effort; delivery self-heals on the next explicit enable */ }
}

export async function disablePush() {
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
      await api.post("/web-push/unsubscribe", { endpoint: sub.endpoint });
      await sub.unsubscribe();
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: "failed" };
  }
}
